/**
 * Shell behaviour: the language switcher, the ad units, and the tool itself.
 *
 * The tool ships as its own lazy-loaded module so this entry stays small and
 * the per-page budget (60KB gz) has room.
 */

import { config } from './i18n.js';
import { urlForLocale } from './urls.js';
import { initAds } from './ads.js';

const LANG_KEY = 'lang';

function initLanguageSwitcher() {
  const sheet = document.getElementById('lang-sheet');
  const openBtn = document.querySelector('[data-open-lang]');
  if (!sheet || !openBtn) return;

  openBtn.addEventListener('click', () => sheet.showModal());

  sheet.querySelector('[data-close-lang]')?.addEventListener('click', () => sheet.close());

  // Tapping the backdrop closes the sheet.
  sheet.addEventListener('click', (event) => {
    if (event.target === sheet) sheet.close();
  });

  for (const button of sheet.querySelectorAll('[data-lang]')) {
    button.addEventListener('click', () => {
      const locale = button.dataset.lang;
      try {
        localStorage.setItem(LANG_KEY, locale);
      } catch {
        // Private mode: the URL prefix still carries the choice.
      }

      /*
       * Also a cookie, because the redirect at `/` is decided on the server
       * before any script runs. localStorage was written here and read by
       * nothing, so a chosen language lasted exactly as long as the URL.
       *
       * One year, Lax, no personal data — a functional preference the visitor
       * asked for by tapping this button.
       */
      document.cookie = `${LANG_KEY}=${encodeURIComponent(locale)};path=/;max-age=31536000;samesite=lax`;
      window.location.assign(urlForLocale(window.location.pathname, locale, config.locales));
    });
  }
}

async function init() {
  initLanguageSwitcher();

  // Server-rendered ad units need one push each; deferred to the load event.
  initAds();

  // Each screen is one lazy-loaded module, so a page never pays for a feature
  // it does not show. The lookup covers the home form and the report shell.
  if (document.querySelector('[data-lookup], [data-report-shell], [data-share]')) {
    const { initPlayer } = await import('./player.js');
    initPlayer();
  }

  // Every field that takes a player gets the name autocomplete — the home
  // lookup and both sides of the compare form.
  if (document.querySelector('[data-player-input]')) {
    const { initSuggest } = await import('./suggest.js');
    initSuggest();
  }

  // Only the distribution article carries the chart, so only it pays for this.
  if (document.querySelector('[data-curve]')) {
    const { initCurve } = await import('./curve.js');
    initCurve();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
